import React, { createRef, useEffect, useRef, useState } from 'react'
import IntentCard from './IntentCard'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'

interface IProps {
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  data: any
}

const ManageInstagramBot: React.FC<IProps> = ({ inputJson, setInputJson, data }) => {
  const intentElementRefs = useRef<any[]>([])
  const addIntentDropdownMenuRef = useRef<any[]>([])

  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const params = useParams()
  const uuid = params.uuid
  const navigate = useNavigate()

  intentElementRefs.current = inputJson.map(
    (item: any, i: number) => intentElementRefs.current[i] ?? createRef()
  )
  addIntentDropdownMenuRef.current = inputJson.map(
    (item: any, i: number) => addIntentDropdownMenuRef.current[i] ?? createRef()
  )

  const scrollToIntent = (intentId: number) => {
    const targetIntentIndex = inputJson.findIndex((item) => item.id === intentId)
    if (!!intentElementRefs?.current && intentElementRefs.current.length > 0) {
      ;(intentElementRefs.current[targetIntentIndex].current as HTMLDivElement).scrollIntoView({
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    if (!!inputJson && inputJson.length > 0) {
      intentElementRefs.current = inputJson.map(
        (item: any, i: number) => intentElementRefs.current[i] ?? createRef()
      )
      addIntentDropdownMenuRef.current = inputJson.map(
        (item: any, i: number) => addIntentDropdownMenuRef.current[i] ?? createRef()
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputJson])

  const checkInstagramConfig = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      if (!token) {
        toast.error('No auth token found. Please log in again.')
        return
      }

      const res = await axios.get(
        `${process.env.REACT_APP_RESUME_PARSER_URL}/get-instagramConfig?bgAccountId=${data.bgAccountId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (res.data?.config && res.data.config.length > 0) {
        setShowPublishModal(true)
      } else {
        setShowConfigModal(true)
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.info(error.response.data?.message || 'Instagram configuration not found.')
        setShowConfigModal(true)
      } else {
        console.error('Error checking Instagram config:', error)
        toast.error('Failed to check Instagram configuration.')
      }
    }
  }

  const handlePublish = async () => {
    try {
      setPublishing(true)
      const token = localStorage.getItem('auth-token')

      if (!token) {
        toast.error('No auth token found. Please log in again.')
        return
      }

      await axios.put(
        `${process.env.REACT_APP_RESUME_PARSER_URL}/publish-instagram-Bot`,
        { applicationId: data.id },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      toast.success('Instagram bot published successfully!')
      setShowPublishModal(false)
    } catch (error) {
      console.error('Error publishing Instagram bot:', error)
      toast.error('Failed to publish Instagram bot.')
    } finally {
      setPublishing(false)
    }
  }

  // const handleNavigateToInstallBot = () => {
  //   navigate(`/instagram-bots/bot-builder/install-bot/${uuid}`)
  // }

  return (
    <>
      <div className='card scroll-y h-800px px-3 py-2'>
        <div className='d-flex align-items-start'>
          <h1 className='h1 p-4 lh-lg'>Manage Bot</h1>
          <span className='mt-5 badge bg-success align-self-baseline'>New</span>

          <div className='mx-auto me-10'>
            {/* <button
              className='btn btn-light-danger border border-danger align-self-start mt-4 px-4 py-2 me-2'
              onClick={handleNavigateToInstallBot}
            >
              <i className='bi bi-instagram'></i> Use this bot
            </button> */}
            <button
              className='btn btn-success border border-success align-self-start mt-4 px-4 py-2'
              onClick={checkInstagramConfig}
            >
              Publish
            </button>
          </div>
        </div>

        {inputJson.map((intent, index) => (
          <div ref={intentElementRefs.current[index]} key={intent.id}>
            <IntentCard
              intent={intent}
              scrollToIntent={scrollToIntent}
              inputJson={inputJson}
              setInputJson={setInputJson}
              addIntentRef={addIntentDropdownMenuRef?.current[index]}
              data={data}
            />
          </div>
        ))}
      </div>

      {/* Publish Confirmation Modal */}
      {showPublishModal && (
        <div className='modal fade show d-block' tabIndex={-1}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Confirm Publish</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setShowPublishModal(false)}
                ></button>
              </div>
              <div className='modal-body'>
                <p>Are you sure you want to publish this Instagram bot?</p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setShowPublishModal(false)}
                  disabled={publishing}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  onClick={handlePublish}
                  disabled={publishing}
                >
                  {publishing ? 'Publishing...' : 'Yes, Publish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Missing Config Modal */}
      {showConfigModal && (
        <div className='modal fade show d-block' tabIndex={-1}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Configuration Required</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setShowConfigModal(false)}
                ></button>
              </div>
              <div className='modal-body'>
                <p>
                  Please complete the configuration first in the configuration page to access the
                  Instagram bot.
                </p>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={() => setShowConfigModal(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ManageInstagramBot
